import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import FindProgress, { useBeaconFinds } from "../../components/FindProgress";
import { phoneUi } from "../ui/phoneUi";
import { useFeatures } from "../../utils/features";
import {
  BEACON_LIST_REFRESH_MS,
  assignBeaconByTrk,
  batteryLabel,
  beaconCounts,
  beaconState,
  soPalletText, crateText,
  beaconWhere,
  chargeBadge,
  currentActor,
  firmwareNote,
  loadBeacons,
  normalizeBeaconId,
  screenStatus,
} from "../../utils/beacons";
import { loadLotDetail, lotStatusLabel } from "../../features/materials/lots";
import { useBeaconFind } from "../../features/materials/lotBeacons";
import { salesOrderPalletPath } from "../../utils/scanCodes";
import { BADGE_TONE } from "./materials/BeaconFind.jsx";
import "../mobile.css";

// The phone Beacons page (plan Phase 1): every pallet beacon with its battery, where it is, the
// pallet it is on (a TRK opens the lot, read only; a sales-order pallet links to its order) and
// Find. ?beacon=BCN-003 (an available beacon's screen QR) picks that beacon out, and a free one
// can be put on a pallet by its TRK number. Adding, retiring and the gateways stay on the desktop page. Behind the pallet_beacons
// switch like the desktop page. Layout only: utils/beacons.js, features/materials/lots.js.

export default function BeaconsScreen() {
  const { palletBeacons, loaded } = useFeatures();
  const location = useLocation();
  const focusId = normalizeBeaconId(new URLSearchParams(location.search).get("beacon"));
  const [beacons, setBeacons] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [openMtn, setOpenMtn] = useState(null);
  const [lotText, setLotText] = useState("");
  const [assigning, setAssigning] = useState(false);
  const focusRef = useRef(null);
  const scrolledTo = useRef(null);
  const finder = useBeaconFinds(beacons);

  const load = useCallback(async () => {
    const r = await loadBeacons();
    if (r.ok && r.body) {
      setBeacons(r.body.beacons || []);
      setError(null);
    } else {
      setError(r.message);
    }
  }, []);
  const findAction = useBeaconFind(finder, load);
  const finding = findAction.finding;

  useEffect(() => {
    if (!palletBeacons.enabled) return undefined;
    load();
    const t = setInterval(load, BEACON_LIST_REFRESH_MS);
    return () => clearInterval(t);
  }, [palletBeacons.enabled, load]);

  // Bring the scanned beacon into view once, when the list first shows it.
  useEffect(() => {
    if (!focusId || !beacons || scrolledTo.current === focusId) return;
    const el = focusRef.current;
    if (el) {
      scrolledTo.current = focusId;
      if (el.scrollIntoView) el.scrollIntoView({ block: "center" });
    }
  }, [focusId, beacons]);

  async function assign(e) {
    e.preventDefault();
    setMsg(null);
    setAssigning(true);
    const said = await assignBeaconByTrk({ beaconId: focusId, lotText, ui: phoneUi, actor: currentActor() });
    setAssigning(false);
    if (said.cancelled) return;
    setMsg({ tone: said.tone, text: said.text });
    if (said.assigned) {
      setLotText("");
      load();
    }
  }

  // Find by beacon (features/materials/lotBeacons, shared with the phone material's lots): a followable
  // answer starts its card's progress (queued -> sent -> lit), an older API's answer is the sentence.
  async function find(b) {
    const said = await findAction.find(b);
    setMsg(said.find ? null : { tone: said.tone, text: said.text });
  }

  if (!loaded) {
    return (
      <div className="jq-list-state" role="status" data-testid="phone-beacons">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
      </div>
    );
  }
  if (!palletBeacons.enabled) {
    return (
      <div className="jq-screen" data-testid="phone-beacons">
        <div className="jq-note jq-note-info">Pallet beacons aren't switched on here yet.</div>
      </div>
    );
  }

  const list = beacons || [];
  const counts = beaconCounts(list);
  const focused = focusId ? list.find((b) => b.beacon_id === focusId) || null : null;
  const focusKind = beaconState(focused);
  // Retired beacons last; otherwise the API's order.
  const rows = [...list.filter((b) => b.state !== "retired"), ...list.filter((b) => b.state === "retired")];

  return (
    <div className="jq-screen" data-testid="phone-beacons">
      <div className="d-flex flex-wrap gap-2 mb-2" data-testid="phone-beacon-counts">
        <span className="jq-tag">{counts.total} beacons</span>
        <span className="jq-tag jq-tag-next">{counts.onPallets} on pallets</span>
        <span className="jq-tag">{counts.free} free</span>
        {counts.low > 0 && <span className="jq-tag jq-tag-block">{counts.low} low battery</span>}
      </div>

      {msg && <div className={`jq-note ${msg.tone === "danger" ? "jq-note-danger" : "jq-note-info"}`} role="status">{msg.text}</div>}
      {error && <div className="jq-note jq-note-danger" role="alert">{error}</div>}

      {focusId && beacons && (
        <div className="jq-note jq-note-info" data-testid="phone-beacon-focus">
          {focusKind === "unregistered" && `${focusId} isn't registered yet. Switch it on near a gateway, or add it on the desktop Beacons page.`}
          {focusKind === "retired" && `${focusId} is out of service.`}
          {focusKind === "on_pallet" && `${focusId} is on TRK ${focused.lot.trk}.`}
          {focusKind === "on_so_pallet" && `${focusId} is on ${soPalletText(focused.pallet)}.`}
          {focusKind === "on_crate" && `${focusId} is on a ${crateText(focused.crate)}.`}
          {focusKind === "free" && (
            <form onSubmit={assign}>
              <div className="fw-semibold mb-2">Put {focusId} on a pallet</div>
              <label htmlFor="phone-beacon-trk" className="form-label mb-1">TRK number (on the pallet's material label)</label>
              <div className="jq-row">
                <input
                  id="phone-beacon-trk"
                  className="form-control form-control-lg"
                  inputMode="numeric"
                  autoComplete="off"
                  data-own-scans /* a scanned pallet lot QR belongs here, not to ScanWedge */
                  placeholder="000409"
                  value={lotText}
                  disabled={assigning}
                  onChange={(e) => setLotText(e.target.value)}
                />
                <button type="submit" className="btn btn-primary btn-lg" disabled={assigning || !lotText.trim()}>
                  {assigning ? "…" : "Assign"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {beacons === null && !error && (
        <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /></div>
      )}
      {beacons && beacons.length === 0 && (
        <div className="jq-list-state">No beacons yet. Switch one on near a gateway, or add it by number on the desktop page.</div>
      )}

      <div data-testid="phone-beacon-list">
        {rows.map((b) => {
          const badge = chargeBadge(b);
          const scr = screenStatus(b);
          const fwNote = firmwareNote(b);
          const retired = b.state === "retired";
          const isFocus = b.beacon_id === focusId;
          return (
            <div
              key={b.beacon_id}
              ref={isFocus ? focusRef : undefined}
              className="jq-card"
              style={isFocus ? { borderColor: "var(--jq-ph-accent)", borderWidth: 2 } : retired ? { opacity: 0.6 } : undefined}
              data-testid={`phone-beacon-${b.beacon_id}`}
              aria-current={isFocus ? "true" : undefined}
            >
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold font-monospace">{b.beacon_id}</span>
                <span className={badge ? "fw-bold" : ""}>{batteryLabel(b)}</span>
                {b.charging ? <span className="jq-tag jq-tag-next">charging</span> : null}
                {badge && <span className={`jq-tag ${BADGE_TONE[badge.className] || ""}`} data-testid="charge-badge">{badge.text}</span>}
              </div>
              {fwNote && fwNote.tone !== "muted" && (
                <div className="jq-muted small mt-1" data-testid="phone-firmware-note">{fwNote.text}</div>
              )}
              <div className="mt-1">
                {b.lot ? (
                  <>
                    <button type="button" className="btn btn-link p-0 fw-semibold" onClick={() => setOpenMtn(b.lot.mtn_id)}>
                      TRK {b.lot.trk}
                    </button>
                    <span className="jq-muted">{` · ${b.lot.material || ""} ${b.lot.size || ""}`.trimEnd()}</span>
                  </>
                ) : b.pallet ? (
                  <Link className="fw-semibold" data-testid="phone-beacon-pallet"
                    to={salesOrderPalletPath(b.pallet.sales_order_id, b.pallet.pallet_id)}>{soPalletText(b.pallet)}</Link>
                ) : (
                  <span className="jq-muted">{retired ? "retired" : "free"}</span>
                )}
                {b.lot_done && <div className="jq-muted text-warning">lot done: frees on next check-in</div>}
              </div>
              <div className="jq-muted">{beaconWhere(b)}{scr.text ? ` · ${scr.text}` : ""}</div>
              {!retired && (
                <button
                  type="button"
                  className="btn btn-success btn-lg w-100 mt-2"
                  disabled={finding === b.beacon_id || finder.busy(b.beacon_id)}
                  onClick={() => find(b)}
                >
                  <i className="fas fa-lightbulb me-2" aria-hidden="true" />
                  {finding === b.beacon_id || finder.busy(b.beacon_id) ? "Finding…" : `Find ${b.beacon_id}`}
                </button>
              )}
              {finder.finds[b.beacon_id] && (
                <FindProgress find={finder.finds[b.beacon_id]} onRetry={() => find(b)}
                  onChange={finder.track(b.beacon_id)} onSettled={load} className="mt-2" />
              )}
            </div>
          );
        })}
      </div>

      {openMtn && <LotPanel mtnId={openMtn} onClose={() => setOpenMtn(null)} />}
    </div>
  );
}

// A pallet (TRK), read only: what it is, how much is left, who it's held for. Changes to a lot are
// made on the desktop TRK popup.
function LotPanel({ mtnId, onClose }) {
  const [lot, setLot] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    loadLotDetail(mtnId)
      .then((d) => { if (live) setLot(d); })
      .catch((err) => { if (live) setError(String(err) || "Couldn't load this pallet."); });
    return () => { live = false; };
  }, [mtnId]);

  const trk = lot && lot.tracking_number_display ? lot.tracking_number_display : String(mtnId).padStart(6, "0");
  return (
    <FullScreenPanel open title={`TRK ${trk}`} onClose={onClose} testId="phone-lot">
      {error && <div className="jq-note jq-note-danger" role="alert">{error}</div>}
      {!lot && !error && (
        <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /></div>
      )}
      {lot && (
        <>
          <div className="fw-semibold">{lot.material_description || lot.material_code}</div>
          {lot.earmark_text && <div className="jq-note jq-note-info mt-2">{lot.earmark_text}</div>}
          <dl className="jq-kv">
            <div><dt>Left</dt><dd>{lot.remaining_qty ?? "?"}</dd></div>
            <div><dt>Received</dt><dd>{lot.quantity ?? "?"}</dd></div>
            <div><dt>Status</dt><dd>{lotStatusLabel(lot.lot_status)}</dd></div>
            <div><dt>Heat</dt><dd>{lot.heat_number || "None"}</dd></div>
            {lot.po_number && <div><dt>PO</dt><dd>{lot.po_number}</dd></div>}
            {(lot.supplier || lot.customer_name) && <div><dt>From</dt><dd>{lot.supplier || lot.customer_name}</dd></div>}
          </dl>
          <div className="jq-muted">Earmarks, counts and notes are changed on the desktop TRK popup.</div>
        </>
      )}
    </FullScreenPanel>
  );
}
